import React, {createContext, useEffect, useState} from "react";

import {useAuth0} from "@auth0/auth0-react";
import Store from "../lib/Store";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link
} from 'react-router-dom'
import {LoggedInRoutes} from "../LoggedInRoutes";
import ClustersIndexPage from "./loggedin/clusters/ClustersIndexPage";
import UserSettingsPage from "./loggedin/profile/UserSettingsPage";
import {ToastContainer} from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import CreateClusterPage from "./loggedin/clusters/mgmt/CreateClusterPage";
import ClusterOverviewPage from "./loggedin/clusters/overview/ClusterOverviewPage";
import NodesPage from "./loggedin/clusters/nodes/NodesPage";
import NotFoundPage from "./misc/NotFoundPage";
import ClusterHealthPage from "./loggedin/clusters/health/ClusterHealthPage";
import OuisPage from "./loggedin/clusters/data/ouis/OuisPage";
import BluetoothPage from "./loggedin/clusters/data/bluetooth/BluetoothPage";
import GeoIpPage from "./loggedin/clusters/data/geoip/GeoIpPage";
import ClusterApiKeyPage from "./loggedin/clusters/apikey/ClusterApiKeyPage";
import ClusterSettingsPage from "./loggedin/clusters/settings/ClusterSettingsPage";
import SupportPage from "./loggedin/clusters/support/SupportPage";
import TapsPage from "./loggedin/clusters/taps/TapsPage";
import UserMenu from "./ui/UserMenu";
import NavigationRail from "./ui/NavigationRail";
import MobileNavigation from "./ui/MobileNavigation";
import useUserService from "../services/UserService";
import useClusterService from "../services/ClusterService";
import LoadingDialog from "./misc/LoadingDialog";
import UavPage from "./loggedin/clusters/data/uav/UavPage";
import EnvironmentPage from "./loggedin/clusters/data/environment/EnvironmentPage";

export const BreadcrumbContext = createContext(null);
export const ActionButtonsContext = createContext(null);
export const SidebarContext = createContext(null);
export const HeadlineContext = createContext(null);
export const UserContext = createContext(null);
export const ClustersContext = createContext(null);

export default function LoggedInApp() {

  const { getAccessTokenSilently } = useAuth0();
  const userService = useUserService();
  const clusterService = useClusterService();

  const [stylesReady, setStylesReady] = useState(false);
  const [apiConnectionReady, setApiConnectionReady] = useState(false);
  const [user, setUser] = useState(null);
  const [clusters, setClusters] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState(null);
  const [actionButtons, setActionButtons] = useState(null);
  const [sidebar, setSidebar] = useState(null);
  const [headline, setHeadline] = useState(null)

  const loadAccessToken = async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      Store.set("api_token", accessToken);
      setApiConnectionReady(true)
    } catch (e) {
      console.log("Could not fetch API Token.");
    }
  };

  // The list of clusters drives the navigation. Pages that create, rename or delete clusters call this to update it.
  const refreshClusters = (onDone) => {
    clusterService.findAllClusters((data) => {
      setClusters(data.clusters);

      if (onDone) {
        onDone(data.clusters);
      }
    });
  }

  useEffect(() => {
    // Nothing is rendered before the stylesheets are in place. Charts size themselves to their container when
    // they mount, so they must not see the unstyled layout. The stylesheets are loaded one after the other so the
    // colors always end up after the layout in the cascade.
    import("../loggedin.css")
        .then(() => import("../loggedin-colors.css"))
        .then(() => setStylesReady(true));

    const apiToken = Store.get("api_token");

    if (apiToken) {
      setApiConnectionReady(true);
    } else {
      loadAccessToken();
    }
  }, []);

  useEffect(() => {
    if (apiConnectionReady && user == null) {
      userService.findOwnUser(setUser);
    }
  }, [apiConnectionReady, user]);

  useEffect(() => {
    if (apiConnectionReady) {
      refreshClusters();
    }
  }, [apiConnectionReady]);

  const mainContent = () => {
    if (!apiConnectionReady || !user) {
      return <LoadingDialog />
    }

    return (
        <Routes>
          { /* Clusters / Details. */}
          <Route path={LoggedInRoutes.CLUSTERS.INDEX} element={<ClustersIndexPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.SHOW(":uuid")} element={<ClusterOverviewPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.NODES(":uuid")} element={<NodesPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.TAPS(":uuid")} element={<TapsPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.HEALTH(":uuid")} element={<ClusterHealthPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.SUPPORT(":uuid")} element={<SupportPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.APIKEY(":uuid")} element={<ClusterApiKeyPage />}/>

          { /* Clusters / Data. */}
          <Route path={LoggedInRoutes.CLUSTERS.DATA.OUIS(":uuid")} element={<OuisPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.DATA.BLUETOOTH(":uuid")} element={<BluetoothPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.DATA.GEOIP(":uuid")} element={<GeoIpPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.DATA.UAV(":uuid")} element={<UavPage />}/>
          <Route path={LoggedInRoutes.CLUSTERS.DATA.ENVIRONMENT(":uuid")} element={<EnvironmentPage />}/>

          { /* Clusters / Settings. */}
          <Route path={LoggedInRoutes.CLUSTERS.SETTINGS.INDEX(":uuid")} element={<ClusterSettingsPage />}/>

          <Route path={LoggedInRoutes.CLUSTERS.MGMT.CREATE} element={<CreateClusterPage />}/>

          { /* User Settings / Own User. */}
          <Route path={LoggedInRoutes.USER.SETTINGS} element={<UserSettingsPage />}/>

          { /* Catch-all / 404. */}
          <Route path="*" element={<NotFoundPage />}/>
        </Routes>
    )
  }

  if (!stylesReady) {
    return <LoadingDialog />
  }

  return (
      <Router>
        <UserContext.Provider value={{user: user, setUser: setUser}}>
          <ClustersContext.Provider value={{clusters: clusters, refresh: refreshClusters}}>
            <BreadcrumbContext.Provider value={{set: setBreadcrumbs}}>
              <ActionButtonsContext.Provider value={{set: setActionButtons}}>
                <SidebarContext.Provider value={{set: setSidebar}}>
                  <HeadlineContext.Provider value={{set: setHeadline}}>
                    <ToastContainer position="top-center" theme="dark" />

                    <div className="app">
                      <aside id="nav-side" className="d-none d-md-flex">
                        <p className="brand">
                          <Link className="brand-link" to={LoggedInRoutes.CLUSTERS.INDEX}>
                            <img src="/static/img/nzyme-logo.svg" alt="Nzyme" />
                            <span>Connect</span>
                          </Link>
                        </p>

                        <div className="user-profile">
                          <UserMenu />
                        </div>

                        <NavigationRail clusters={clusters} sidebar={sidebar} />
                      </aside>

                      <div id="main">
                        <div className="topbar-sm d-md-none">
                          <Link className="brand-link" to={LoggedInRoutes.CLUSTERS.INDEX}>
                            <img src="/static/img/nzyme-logo.svg" alt="Nzyme" />
                            <span>Connect</span>
                          </Link>

                          <UserMenu />
                        </div>

                        <MobileNavigation clusters={clusters} sidebar={sidebar} />

                        <div className="content">
                          <div className="content-header">
                            <div className="headline">
                              {breadcrumbs}
                              {headline}
                            </div>

                            <div className="action-buttons">
                              {actionButtons}
                            </div>
                          </div>

                          <main>
                            {mainContent()}
                          </main>
                        </div>

                        <footer>
                          pugnantis latus defensantes
                        </footer>
                      </div>
                    </div>
                  </HeadlineContext.Provider>
                </SidebarContext.Provider>
              </ActionButtonsContext.Provider>
            </BreadcrumbContext.Provider>
          </ClustersContext.Provider>
        </UserContext.Provider>
      </Router>
  )

}
